import React, { createContext, useContext, useMemo } from 'react';

import { useStoreContext } from 'features/store';

import { IBaseProps } from '@rbi-ctg/frontend';
import { ServiceMode } from 'generated/graphql-gateway';
import { getPosVendorFromStore } from 'hooks/menu/get-pos-vendor-from-store';
import { PosServiceMode } from 'state/unified-menu/types';
import { getPosServiceMode } from 'state/unified-menu/utils';
import { PosVendors } from 'utils/vendor-config';

import { useServiceModeLive } from './index';

interface IDerivedServiceModeCtx {
  posServiceMode: PosServiceMode;
  posVendor: PosVendors | null;
  isDelivery: boolean;
}

interface IDriveThruCtx {
  isDriveThru: boolean;
  isMobileOrderForDriveThru: boolean;
}

const DerivedServiceModeContext = createContext<IDerivedServiceModeCtx>({
  posServiceMode: 'pickup',
  posVendor: null,
  isDelivery: false,
});

const DriveThruContext = createContext<IDriveThruCtx>({
  isDriveThru: false,
  isMobileOrderForDriveThru: false,
});

export const useDerivedServiceModeContext = () => useContext(DerivedServiceModeContext);
export const useDriveThruContext = () => useContext(DriveThruContext);

/**
 * Provides derived values that only change when the derivation result
 * changes — not on every raw serviceMode switch.
 *
 * Pickup/delivery values (`posServiceMode`, `posVendor`, `isDelivery`) are
 * in a separate context from drive-thru flags (`isDriveThru`,
 * `isMobileOrderForDriveThru`) so that TAKEOUT↔DRIVE_THRU switches don't
 * re-render consumers that only care about the pickup/delivery distinction.
 */
export const DerivedServiceModeProvider = ({ children }: IBaseProps) => {
  const serviceMode = useServiceModeLive();
  const { store } = useStoreContext();

  const posServiceMode = getPosServiceMode((serviceMode ?? ServiceMode.DRIVE_THRU) as ServiceMode);
  const posVendor = getPosVendorFromStore(store, serviceMode);
  const isDelivery = posServiceMode === 'delivery';

  const derivedValue = useMemo(() => ({ posServiceMode, posVendor, isDelivery }), [
    posServiceMode,
    posVendor,
    isDelivery,
  ]);

  const isDriveThru = serviceMode === ServiceMode.DRIVE_THRU;
  const isMobileOrderForDriveThru = serviceMode === ServiceMode.MOBILE_ORDER_DRIVE_THRU;

  const driveThruValue = useMemo(() => ({ isDriveThru, isMobileOrderForDriveThru }), [
    isDriveThru,
    isMobileOrderForDriveThru,
  ]);

  return (
    <DerivedServiceModeContext.Provider value={derivedValue}>
      <DriveThruContext.Provider value={driveThruValue}>{children}</DriveThruContext.Provider>
    </DerivedServiceModeContext.Provider>
  );
};
